/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useQuery } from '@apollo/client';

import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import { useTheme } from '@coursera/cds-core';

import { getProductPath, redirectToUnifiedDescriptionPage } from 'bundles/enterprise-legacy-xdp/hocs/withMiniModal';
import type EnterpriseProgramsV1 from 'bundles/naptimejs/resources/enterprisePrograms.v1';
import type ProgramBrowsingExperienceV1 from 'bundles/naptimejs/resources/programBrowsingExperiences.v1';
import type ThirdPartyOrganizationsV1 from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';
import EnterpriseBadgeCollectionsProvider from 'bundles/page-config-common/providers/enterprise/EnterpriseBadgeCollectionsProvider';
import type { Product, ProductType } from 'bundles/program-common/types/programCommon';
import AllBrowseCollections from 'bundles/program-home/components/single-program/AllBrowseCollections';
import AllCuratedCollections from 'bundles/program-home/components/single-program/AllCuratedCollections';
import CollectionsBreadcrumbs from 'bundles/program-home/components/single-program/CollectionsBreadcrumbs';
import { COLLECTION_TYPE } from 'bundles/program-home/components/single-program/SingleProgramConstants';
import type {
  AcademicDisciplineNameQueryVariables,
  AcademicDisciplineSummariesResult,
} from 'bundles/program-home/components/single-program/SingleProgramGraphqlQueries';
import { AcademicDisciplineNameQuery } from 'bundles/program-home/components/single-program/SingleProgramGraphqlQueries';
import { getCatalogCollectionProps } from 'bundles/program-home/components/single-program/withCatalogCollectionProps';
import type { OnProductCardClick } from 'bundles/program-home/types/Products';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useBrowsingExperienceType, useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';

export type PropsFromCaller = {
  programId: string;
  program: EnterpriseProgramsV1;
  thirdPartyOrganizationId: string;
  thirdPartyOrganization: ThirdPartyOrganizationsV1;
  isProgramMember: boolean;
  isProgramAdmin: boolean;
  isAuthenticatedUser: boolean;
  browsingExperienceType?: ProgramBrowsingExperienceV1;
  isBrowseOnlyProgram: boolean;
  shouldShowSponsoredByMessage: boolean;
  enableCurriculumBuilder: boolean;
  enableAcademicDisciplines?: boolean;
  allowOrgForCurriculumBuilder?: boolean;
  allowOrgForSpecializationConfiguration?: boolean;
  enableSkillsInSearchAndBrowse: boolean;
};

type CollectionsViewProps = {
  collectionType: COLLECTION_TYPE;
  programId: string;
  programSlug: string;
  isAuthenticatedUser: boolean;
  thirdPartyOrganizationId: string;
  onProductCardClick: OnProductCardClick;
  enableCurriculumBuilder: boolean;
  academicDisciplineName?: string;
  allowOrgForSpecializationConfiguration?: boolean;
  catalogCollectionLength: number;
};

export function CollectionsView({
  collectionType,
  programId,
  programSlug,
  onProductCardClick,
  isAuthenticatedUser,
  catalogCollectionLength,
  thirdPartyOrganizationId,
  enableCurriculumBuilder,
  academicDisciplineName,
  allowOrgForSpecializationConfiguration,
}: CollectionsViewProps) {
  switch (collectionType) {
    case COLLECTION_TYPE.CURATED:
      return (
        <AllCuratedCollections
          programId={programId}
          programSlug={programSlug}
          onProductCardClick={onProductCardClick}
          limit={6}
          thirdPartyOrganizationId={thirdPartyOrganizationId}
          enableCurriculumBuilder={enableCurriculumBuilder}
          allowOrgForSpecializationConfiguration={allowOrgForSpecializationConfiguration}
          academicDisciplineName={academicDisciplineName}
          isAllCollectionsPage
        />
      );
    case COLLECTION_TYPE.BROWSE:
      return (
        <AllBrowseCollections
          programId={programId}
          programSlug={programSlug}
          isAuthenticatedUser={isAuthenticatedUser}
          onProductCardClick={onProductCardClick}
          limit={catalogCollectionLength}
          allowOrgForSpecializationConfiguration={allowOrgForSpecializationConfiguration}
        />
      );
    default:
      return null;
  }
}

export function AllCollectionsPage(): JSX.Element | null {
  const router = useRouter();
  const program = useProgram();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const browsingExperienceType = useBrowsingExperienceType();

  const { allowOrgForCurriculumBuilder, enableAcademicDisciplines, allowOrgForSpecializationConfiguration } =
    useEnterpriseProductFeatures() || {};

  const {
    params: { programSlug },
  } = router;

  const theme = useTheme();
  const isAuthenticatedUser = user.isAuthenticatedUser();

  const collectionType = router.params.collectionType as COLLECTION_TYPE;

  const { data } = useQuery<AcademicDisciplineSummariesResult, AcademicDisciplineNameQueryVariables>(
    AcademicDisciplineNameQuery,
    {
      skip: !program?.metadata?.linkedAcademicDisciplineIds?.length,
      variables: {
        disciplineId: program?.metadata.linkedAcademicDisciplineIds?.[0] ?? '',
      },
      context: { clientName: 'gatewayGql' },
    }
  );

  const academicDisciplineName = data?.AcademicDiscipline?.queryAcademicDisciplineById?.name;

  const onProductCardClick = React.useCallback(
    (product: Product, productType: ProductType, collectionId?: string | null) => {
      if (productType === 'clip') {
        window.open(`/videos/${product.slug}?program=${programSlug}`, '_blank');
      } else {
        redirectToUnifiedDescriptionPage(
          router,
          {
            slug: product.slug,
            productPagePath: getProductPath(product),
          },
          collectionId ?? undefined
        );
      }
    },
    [router, programSlug]
  );

  const { catalogCollectionLength } = getCatalogCollectionProps({
    program: program as EnterpriseProgramsV1,
    browsingExperienceType,
    shouldHideCourseraRecommendations: false,
  });

  const styles = {
    container: css`
      background-color: var(--cds-color-white-0);
    `,
    main: css`
      margin: 0 auto;
      padding: 0 12px;
    `,
  };

  if (!program || !thirdPartyOrganization) {
    return null;
  }

  return (
    <EnterpriseBadgeCollectionsProvider programFromProps={program}>
      <CollectionsBreadcrumbs
        collectionType={collectionType}
        programSlug={programSlug}
        programName={program.metadata.name}
        thirdPartyOrgSlug={thirdPartyOrganization.slug}
        thirdPartyOrganizationId={thirdPartyOrganization.id}
      />
      <main css={styles.main}>
        <CollectionsView
          programId={program.id}
          programSlug={programSlug}
          collectionType={collectionType}
          isAuthenticatedUser={isAuthenticatedUser}
          onProductCardClick={onProductCardClick}
          catalogCollectionLength={catalogCollectionLength}
          thirdPartyOrganizationId={thirdPartyOrganization.id}
          enableCurriculumBuilder={Boolean(allowOrgForCurriculumBuilder)}
          academicDisciplineName={enableAcademicDisciplines ? academicDisciplineName : undefined}
          allowOrgForSpecializationConfiguration={allowOrgForSpecializationConfiguration}
        />
      </main>
    </EnterpriseBadgeCollectionsProvider>
  );
}

export default AllCollectionsPage;
